import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, Save, X, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Activities = () => {
  const [season, setSeason] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activities, setActivities] = useState([]);
  const [newActivity, setNewActivity] = useState({ date: "", description: "", time: "", end_time: "" });
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({ date: "", description: "", time: "", end_time: "" });
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const seasonsRes = await axios.get(`${API}/seasons`);
        if (seasonsRes.data.length > 0) {
          const current = seasonsRes.data[0];
          setSeason(current);
          const actRes = await axios.get(`${API}/activities?season_id=${current.id}`);
          setActivities(actRes.data);
        }
      } catch (error) {
        toast.error("Activiteiten laden mislukt");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleAddActivity = async () => {
    if (!newActivity.description) {
      toast.error("Omschrijving is verplicht");
      return;
    }
    try {
      const res = await axios.post(`${API}/activities`, {
        ...newActivity,
        season_id: season.id,
      });
      setActivities(prev => [...prev, res.data].sort((a, b) => (a.date || "").localeCompare(b.date || "")));
      setNewActivity({ date: "", description: "", time: "", end_time: "" });
      toast.success("Activiteit toegevoegd");
    } catch (error) {
      toast.error("Activiteit toevoegen mislukt");
    }
  };

  const startEdit = (activity) => {
    setEditingId(activity.id);
    setEditData({
      date: activity.date || "",
      description: activity.description || "",
      time: activity.time || "",
      end_time: activity.end_time || "",
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditData({ date: "", description: "", time: "", end_time: "" });
  };

  const handleSaveEdit = async (activityId) => {
    if (!editData.description) {
      toast.error("Omschrijving is verplicht");
      return;
    }
    try {
      const res = await axios.put(`${API}/activities/${activityId}`, {
        ...editData,
        season_id: season.id,
      });
      setActivities(prev =>
        prev.map(a => (a.id === activityId ? res.data : a)).sort((a, b) => (a.date || "").localeCompare(b.date || ""))
      );
      cancelEdit();
      toast.success("Activiteit bijgewerkt");
    } catch (error) {
      toast.error("Activiteit bijwerken mislukt");
    }
  };

  const handleDeleteActivity = async () => {
    if (!deleteConfirm) return;
    try {
      await axios.delete(`${API}/activities/${deleteConfirm}`);
      setActivities(prev => prev.filter(a => a.id !== deleteConfirm));
      toast.success("Activiteit verwijderd");
    } catch (error) {
      toast.error("Activiteit verwijderen mislukt");
    } finally {
      setDeleteConfirm(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-400">Laden...</div>
      </div>
    );
  }

  if (!season) {
    return (
      <div className="max-w-3xl mx-auto text-center py-12">
        <p className="text-slate-500">Nog geen seizoen aangemaakt.</p>
      </div>
    );
  }

  return (
    <div data-testid="activities-page" className="max-w-3xl mx-auto space-y-8">
      {/* Header */}
      <div>
        <h1
          className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-slate-900"
          style={{ fontFamily: 'var(--font-display)' }}
        >
          Activiteiten
        </h1>
        <p className="text-slate-500 mt-2">Beheer team-activiteiten naast de wedstrijden</p>
      </div>

      {/* Activities Card */}
      <Card className="bg-white border border-slate-200 rounded-xl shadow-sm">
        <CardHeader className="border-b border-slate-100 flex flex-row items-center justify-between">
          <CardTitle
            className="text-lg font-semibold uppercase tracking-tight flex items-center gap-2"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            <Calendar className="w-5 h-5" />
            Overzicht
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          {/* Add new activity */}
          <div className="flex flex-col sm:flex-row sm:items-end gap-3">
            <div className="space-y-1 sm:w-36">
              <Label className="text-xs text-slate-500">Datum</Label>
              <Input
                type="date"
                value={newActivity.date}
                onChange={(e) => setNewActivity(prev => ({ ...prev, date: e.target.value }))}
                data-testid="new-activity-date"
              />
            </div>
            <div className="space-y-1 flex-1">
              <Label className="text-xs text-slate-500">Omschrijving</Label>
              <Input
                placeholder="bijv. Team event: paintball & pizza"
                value={newActivity.description}
                onChange={(e) => setNewActivity(prev => ({ ...prev, description: e.target.value }))}
                data-testid="new-activity-description"
              />
            </div>
            <div className="space-y-1 sm:w-24">
              <Label className="text-xs text-slate-500">Van</Label>
              <Input
                type="time"
                value={newActivity.time}
                onChange={(e) => setNewActivity(prev => ({ ...prev, time: e.target.value }))}
                data-testid="new-activity-time"
              />
            </div>
            <div className="space-y-1 sm:w-24">
              <Label className="text-xs text-slate-500">Tot</Label>
              <Input
                type="time"
                value={newActivity.end_time}
                onChange={(e) => setNewActivity(prev => ({ ...prev, end_time: e.target.value }))}
                data-testid="new-activity-end-time"
              />
            </div>
            <Button
              onClick={handleAddActivity}
              className="bg-slate-900 hover:bg-slate-800 flex-shrink-0"
              data-testid="add-activity-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Toevoegen
            </Button>
          </div>

          {/* Activity list */}
          {activities.length > 0 ? (
            <div className="space-y-2">
              {activities.map((activity) => {
                const isEditing = editingId === activity.id;
                if (isEditing) {
                  return (
                    <div
                      key={activity.id}
                      className="flex flex-col sm:flex-row sm:items-end gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200"
                      data-testid={`activity-row-editing-${activity.id}`}
                    >
                      <div className="space-y-1 sm:w-36">
                        <Label className="text-xs text-slate-500">Datum</Label>
                        <Input
                          type="date"
                          value={editData.date}
                          onChange={(e) => setEditData(prev => ({ ...prev, date: e.target.value }))}
                          data-testid={`edit-activity-date-${activity.id}`}
                        />
                      </div>
                      <div className="space-y-1 flex-1">
                        <Label className="text-xs text-slate-500">Omschrijving</Label>
                        <Input
                          value={editData.description}
                          onChange={(e) => setEditData(prev => ({ ...prev, description: e.target.value }))}
                          data-testid={`edit-activity-description-${activity.id}`}
                        />
                      </div>
                      <div className="space-y-1 sm:w-24">
                        <Label className="text-xs text-slate-500">Van</Label>
                        <Input
                          type="time"
                          value={editData.time}
                          onChange={(e) => setEditData(prev => ({ ...prev, time: e.target.value }))}
                          data-testid={`edit-activity-time-${activity.id}`}
                        />
                      </div>
                      <div className="space-y-1 sm:w-24">
                        <Label className="text-xs text-slate-500">Tot</Label>
                        <Input
                          type="time"
                          value={editData.end_time}
                          onChange={(e) => setEditData(prev => ({ ...prev, end_time: e.target.value }))}
                          data-testid={`edit-activity-end-time-${activity.id}`}
                        />
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        <Button
                          onClick={() => handleSaveEdit(activity.id)}
                          className="bg-emerald-600 hover:bg-emerald-700"
                          data-testid={`save-activity-${activity.id}`}
                        >
                          <Save className="w-4 h-4 mr-1" />
                          Opslaan
                        </Button>
                        <Button
                          variant="outline"
                          onClick={cancelEdit}
                          data-testid={`cancel-edit-activity-${activity.id}`}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                }
                return (
                  <div
                    key={activity.id}
                    className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100 group"
                    data-testid={`activity-row-${activity.id}`}
                  >
                    <span className="text-sm font-medium text-slate-500 w-24 flex-shrink-0">
                      {activity.date
                        ? new Date(activity.date).toLocaleDateString("nl-NL", { day: "numeric", month: "short" })
                        : "-"}
                    </span>
                    {(activity.time || activity.end_time) && (
                      <span className="text-sm text-slate-400 w-24 flex-shrink-0">
                        {activity.time || "—"}
                        {activity.end_time ? ` – ${activity.end_time}` : ""}
                      </span>
                    )}
                    <span className="text-sm text-slate-700 flex-1 truncate">{activity.description}</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => startEdit(activity)}
                      className="h-8 w-8 text-slate-400 hover:text-blue-600 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity flex-shrink-0"
                      data-testid={`edit-activity-${activity.id}`}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setDeleteConfirm(activity.id)}
                      className="h-8 w-8 text-slate-400 hover:text-red-500 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity flex-shrink-0"
                      data-testid={`delete-activity-${activity.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-slate-400 text-center py-4" data-testid="activities-empty">
              Nog geen activiteiten toegevoegd
            </p>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Activiteit verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              Deze activiteit wordt permanent verwijderd inclusief eventuele toewijzingen in de planning.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-activity">Annuleren</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteActivity}
              className="bg-red-500 hover:bg-red-600"
              data-testid="confirm-delete-activity"
            >
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Activities;
